import React, { Dispatch, SetStateAction } from 'react';
import Modal from '../modal/Modal';
import { Body2Regular, Body3Regular, Subtitle1Bold } from '../../lib/font';
import { Col, Row } from '../../lib/utils';
import { CodeValueType } from '../../types/types';
import SvgIcon from '../SvgIcon';
import { colors } from '../../lib/colors';
import NewColorSquareButton from '../Button/NewColorSquareButton';
import NewWhiteSquareButton from '../Button/NewWhiteSquareButton';
import NewTextInputDropDown from '../DropDown/NewTextInputDropDown';
import { ChangeCoachFailure, getChangeCoachFailureReason } from '../../lib/changeCoachError';
import ChangeCoachFailureCard, { ChangeCoachInquiryInfo } from './ChangeCoachFailureCard';
type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    dropDownCoachList?: CodeValueType[];
    selectCoach?: CodeValueType;
    sourceCoachList?: CodeValueType[];
    onClickChangeCoach: (newCoachId: string, sourceCoachId?: string) => void;
    failure?: ChangeCoachFailure | null;
    isSubmitting?: boolean;
    inquiryInfo?: ChangeCoachInquiryInfo;
    onRefresh?: () => void;
    onViewMemberships?: () => void;
};

const ModalMemberProfileComponentCoachChange = ({
    setIsVisible,
    dropDownCoachList,
    selectCoach: _selectCoach,
    sourceCoachList,
    onClickChangeCoach,
    failure,
    isSubmitting = false,
    inquiryInfo,
    onRefresh,
    onViewMemberships,
}: Props) => {
    const [sourceCoach, setSourceCoach] = React.useState<CodeValueType | undefined>(
        _selectCoach ?? sourceCoachList?.[0]
    );
    const [targetCoach, setTargetCoach] = React.useState<CodeValueType | undefined>(undefined);
    const [isFailureVisible, setIsFailureVisible] = React.useState(!!failure);
    const sourceCoachOptions = sourceCoachList ?? (_selectCoach ? [_selectCoach] : []);
    const isAssignMode = sourceCoachOptions.length === 0;
    const shouldSelectSourceCoach = sourceCoachOptions.length > 1;
    const targetCoachList = (dropDownCoachList ?? []).filter((coach) => coach.code !== sourceCoach?.code);
    const isSubmitDisabled = isSubmitting || !targetCoach?.code || (!isAssignMode && !sourceCoach?.code);
    const closeModal = () => {
        if (!isSubmitting) setIsVisible(false);
    };
    const retry = () => {
        if (isSubmitDisabled) return;
        onClickChangeCoach(targetCoach?.code ?? '', sourceCoach?.code);
    };
    const visibleFailure = failure && isFailureVisible ? failure : null;
    const failureReason = visibleFailure ? getChangeCoachFailureReason(visibleFailure) : null;
    const failureHeight = visibleFailure
        ? (failureReason === 'share_owner' ? 730 : 660) + (shouldSelectSourceCoach ? 80 : 0)
        : undefined;

    React.useEffect(() => {
        setIsFailureVisible(!!failure);
    }, [failure]);

    React.useEffect(() => {
        if (targetCoach?.code && targetCoach.code === sourceCoach?.code) {
            setTargetCoach(undefined);
        }
    }, [sourceCoach?.code, targetCoach?.code]);

    return (
        <Modal
            whiteViewWidth={1000}
            whiteViewHeight={failureHeight ?? (shouldSelectSourceCoach ? 540 : 480)}
            isCloseButton
            onClose={closeModal}
            isCloseOutside={!isSubmitting}
        >
            <Col className="pt-[36px] px-[48px] pb-[96px]">
                <Subtitle1Bold className=" text-LAVEL_4 mb-[16px]">
                    {isAssignMode ? '담당 강사 배정' : '담당 강사 변경'}
                </Subtitle1Bold>
                <Body3Regular className="text-LAVEL_3 mb-[32px]">
                    {shouldSelectSourceCoach
                        ? '인계할 강사님과 인계 받을 강사님을 선택하시고 주의사항을 읽은 후, ‘변경하기’를 눌러주세요!'
                        : '변경할 강사님을 선택하시고 주의사항을 읽은 후, ‘변경하기’를 눌러주세요!'}
                </Body3Regular>
                {visibleFailure && (
                    <div style={{ marginBottom: 24 }}>
                        <ChangeCoachFailureCard
                            failure={visibleFailure}
                            isRetrying={isSubmitting}
                            inquiryInfo={inquiryInfo}
                            onRetry={retry}
                            onRefresh={onRefresh}
                            onViewMemberships={onViewMemberships}
                        />
                    </div>
                )}
                {shouldSelectSourceCoach && (
                    <Col className="mb-[24px]">
                        <Body3Regular className="text-LAVEL_3 mb-[8px]">인계할 강사</Body3Regular>
                        <NewTextInputDropDown
                            selectData={sourceCoach}
                            datas={sourceCoachOptions}
                            onClickSort={(data) => {
                                setSourceCoach(data);
                                setIsFailureVisible(false);
                            }}
                            width={300}
                            fontType={'Body2Regular'}
                            placeholder="인계할 강사를 선택해주세요"
                        />
                    </Col>
                )}
                <Col className="mb-[40px]">
                    <Body3Regular className="text-LAVEL_3 mb-[8px]">
                        {isAssignMode ? '배정할 강사' : '인계 받을 강사'}
                    </Body3Regular>
                    <NewTextInputDropDown
                        selectData={targetCoach}
                        datas={targetCoachList}
                        onClickSort={(data) => {
                            setTargetCoach(data);
                            setIsFailureVisible(false);
                        }}
                        width={300}
                        fontType={'Body2Regular'}
                        placeholder={isAssignMode ? '배정할 강사를 선택해주세요' : '인계 받을 강사를 선택해주세요'}
                    />
                </Col>

                <Row className=" items-center mb-[8px]">
                    <SvgIcon name={'SvgError'} size={18} fill={colors.red} className="mr-[12px]" />
                    <Body2Regular className=" text-LAVEL_4">강사님이 변경되면 바뀌는 내용을 확인해 주세요!</Body2Regular>
                </Row>
                <Col className="pl-[32px]">
                    <Body3Regular className="text-LAVEL_2">
                        • 이전 강사님과 잡혀 있던 미래 수업 일정이 취소돼요.
                    </Body3Regular>
                    <Body3Regular className="text-LAVEL_2">
                        • 과거 수업 일정, 운동일지, 컨디션 정보 등의 데이터는 회원님이 그대로 확인할 수 있어요.
                    </Body3Regular>
                    <Body3Regular className="text-LAVEL_2">
                        • 이전 강사님 명의로 사용 중인 수강권만 새 담당 강사님께 인계 수강권으로 새로 생성돼요. 다른 강사님의 수강권은 그대로예요.
                    </Body3Regular>
                </Col>
                <Row className=" absolute bottom-[24px] right-[32px]">
                    <NewWhiteSquareButton
                        text="취소"
                        width={148}
                        height={48}
                        mr={16}
                        onClick={closeModal}
                    />
                    <NewColorSquareButton
                        text={isSubmitting ? '변경 중...' : '변경하기'}
                        width={148}
                        height={48}
                        bgColor={'primary'}
                        textColor={'white'}
                        disabled={isSubmitDisabled}
                        onClick={retry}
                    />
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalMemberProfileComponentCoachChange;
